import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  Filter,
  MessageSquare,
  Clock,
  CheckCircle,
  AlertCircle,
  User,
  Send,
  Paperclip,
  MoreHorizontal,
  Tag,
  ChevronLeft,
  ChevronRight,
  ArrowUpRight,
} from 'lucide-react';
import {
  Card,
  Button,
  Input,
  Select,
  Badge,
  Textarea,
  cn,
  StatCard,
  useToast,
} from '@raffle/ui';

interface SupportTicket {
  id: string;
  subject: string;
  category: 'payment' | 'account' | 'draw' | 'technical' | 'other';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  user: {
    id: string;
    name: string;
    email: string;
  };
  assignedTo?: {
    id: string;
    name: string;
  };
  messages: {
    id: string;
    sender: 'user' | 'admin';
    senderName: string;
    content: string;
    timestamp: Date;
  }[];
  createdAt: Date;
  updatedAt: Date;
}

const mockTickets: SupportTicket[] = [
  {
    id: 'SUP-001',
    subject: 'Payment not reflecting in account',
    category: 'payment',
    priority: 'high',
    status: 'open',
    user: { id: 'u1', name: 'Adebayo Johnson', email: 'adebayo@email.com' },
    messages: [
      {
        id: 'm1',
        sender: 'user',
        senderName: 'Adebayo Johnson',
        content: 'I made a payment of ₦150,000 for tickets but it has not reflected in my account after 2 hours.',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
      },
    ],
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 2),
  },
  {
    id: 'SUP-002',
    subject: 'Cannot verify my account',
    category: 'account',
    priority: 'medium',
    status: 'in_progress',
    user: { id: 'u2', name: 'Ngozi Okonkwo', email: 'ngozi@email.com' },
    assignedTo: { id: 'a1', name: 'Admin Support' },
    messages: [
      {
        id: 'm1',
        sender: 'user',
        senderName: 'Ngozi Okonkwo',
        content: 'I have been trying to verify my NIN but the verification keeps failing.',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24),
      },
      {
        id: 'm2',
        sender: 'admin',
        senderName: 'Admin Support',
        content: 'Hello Ngozi, I apologize for the inconvenience. Could you please share the exact error message you are seeing?',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 20),
      },
      {
        id: 'm3',
        sender: 'user',
        senderName: 'Ngozi Okonkwo',
        content: 'It says "Verification service unavailable. Please try again later."',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 18),
      },
    ],
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 18),
  },
  {
    id: 'SUP-003',
    subject: 'Question about draw process',
    category: 'draw',
    priority: 'low',
    status: 'resolved',
    user: { id: 'u3', name: 'Chinedu Eze', email: 'chinedu@email.com' },
    assignedTo: { id: 'a1', name: 'Admin Support' },
    messages: [
      {
        id: 'm1',
        sender: 'user',
        senderName: 'Chinedu Eze',
        content: 'How does the draw process work? Is it truly random?',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 48),
      },
      {
        id: 'm2',
        sender: 'admin',
        senderName: 'Admin Support',
        content: 'Yes! Our draws use a cryptographically secure random number generator. Each draw is independently verified and the audit log is publicly available.',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 46),
      },
    ],
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 48),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 46),
  },
  {
    id: 'SUP-004',
    subject: 'App keeps crashing',
    category: 'technical',
    priority: 'urgent',
    status: 'open',
    user: { id: 'u4', name: 'Folake Adeyemi', email: 'folake@email.com' },
    messages: [
      {
        id: 'm1',
        sender: 'user',
        senderName: 'Folake Adeyemi',
        content: 'The app crashes every time I try to open My Tickets page. I am using iPhone 13 with latest iOS.',
        timestamp: new Date(Date.now() - 1000 * 60 * 30),
      },
    ],
    createdAt: new Date(Date.now() - 1000 * 60 * 30),
    updatedAt: new Date(Date.now() - 1000 * 60 * 30),
  },
];

const statusColors = {
  open: 'warning',
  in_progress: 'info',
  resolved: 'success',
  closed: 'default',
} as const;

const priorityColors = {
  low: 'default',
  medium: 'info',
  high: 'warning',
  urgent: 'error',
} as const;

const categoryLabels = {
  payment: 'Payment',
  account: 'Account',
  draw: 'Draw',
  technical: 'Technical',
  other: 'Other',
};

export const SupportPage: React.FC = () => {
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [replyText, setReplyText] = useState('');
  const [isSending, setIsSending] = useState(false);

  const filteredTickets = mockTickets.filter((ticket) => {
    const matchesSearch =
      ticket.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.user.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || ticket.priority === priorityFilter;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const formatDate = (date: Date) => {
    const diff = Date.now() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    
    setIsSending(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    toast.success('Reply Sent', 'Your response has been sent to the customer');
    setReplyText('');
    setIsSending(false);
  };

  const handleResolve = () => {
    toast.success('Ticket Resolved', 'The support ticket has been marked as resolved');
    setSelectedTicket(null);
  };

  // Stats
  const openTickets = mockTickets.filter((t) => t.status === 'open').length;
  const inProgressTickets = mockTickets.filter((t) => t.status === 'in_progress').length;
  const urgentTickets = mockTickets.filter((t) => t.priority === 'urgent' && t.status !== 'resolved' && t.status !== 'closed').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-display text-2xl font-bold text-text-primary">Support Queue</h1>
        <p className="text-text-secondary">Manage customer support tickets</p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Open Tickets"
          value={openTickets}
          icon={<MessageSquare className="w-5 h-5" />}
        />
        <StatCard
          title="In Progress"
          value={inProgressTickets}
          icon={<Clock className="w-5 h-5" />}
        />
        <StatCard
          title="Urgent"
          value={urgentTickets}
          icon={<AlertCircle className="w-5 h-5" />}
        />
        <StatCard
          title="Resolved Today"
          value={mockTickets.filter((t) => t.status === 'resolved').length}
          icon={<CheckCircle className="w-5 h-5" />}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        {/* Ticket List */}
        <div className="lg:col-span-2 space-y-4">
          {/* Filters */}
          <Card className="p-4">
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                <input
                  type="text"
                  placeholder="Search tickets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-10 pl-10 pr-4 bg-surface-2 border border-white/10 rounded-vault text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold-500/50"
                />
              </div>
              <div className="flex gap-2">
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  options={[
                    { value: 'all', label: 'All Status' },
                    { value: 'open', label: 'Open' },
                    { value: 'in_progress', label: 'In Progress' },
                    { value: 'resolved', label: 'Resolved' },
                  ]}
                  className="flex-1"
                />
                <Select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  options={[
                    { value: 'all', label: 'All Priority' },
                    { value: 'urgent', label: 'Urgent' },
                    { value: 'high', label: 'High' },
                    { value: 'medium', label: 'Medium' },
                    { value: 'low', label: 'Low' },
                  ]}
                  className="flex-1"
                />
              </div>
            </div>
          </Card>

          {/* Ticket Cards */}
          <div className="space-y-3">
            {filteredTickets.map((ticket) => (
              <Card
                key={ticket.id}
                className={cn(
                  'p-4 cursor-pointer transition-all',
                  selectedTicket?.id === ticket.id
                    ? 'border-gold-500/50 bg-gold-500/5'
                    : 'hover:border-white/20'
                )}
                onClick={() => setSelectedTicket(ticket)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-xs text-gold-500">{ticket.id}</span>
                      <Badge variant={priorityColors[ticket.priority] as any} size="sm" className="capitalize">
                        {ticket.priority}
                      </Badge>
                    </div>
                    <h3 className="text-sm font-medium text-text-primary truncate">
                      {ticket.subject}
                    </h3>
                    <p className="text-xs text-text-muted mt-1">
                      {ticket.user.name} · {formatDate(ticket.updatedAt)}
                    </p>
                  </div>
                  <Badge variant={statusColors[ticket.status] as any} size="sm" className="capitalize shrink-0">
                    {ticket.status.replace('_', ' ')}
                  </Badge>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Ticket Detail / Chat */}
        <div className="lg:col-span-3">
          {selectedTicket ? (
            <Card className="flex flex-col h-[calc(100vh-16rem)]">
              {/* Header */}
              <div className="p-4 border-b border-white/10">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-xs text-gold-500">{selectedTicket.id}</span>
                      <Badge variant={statusColors[selectedTicket.status] as any} size="sm" className="capitalize">
                        {selectedTicket.status.replace('_', ' ')}
                      </Badge>
                      <Badge variant={priorityColors[selectedTicket.priority] as any} size="sm" className="capitalize">
                        {selectedTicket.priority}
                      </Badge>
                    </div>
                    <h2 className="font-display text-lg font-semibold text-text-primary">
                      {selectedTicket.subject}
                    </h2>
                  </div>
                  <div className="flex gap-2">
                    {selectedTicket.status !== 'resolved' && (
                      <Button variant="outline" size="sm" onClick={handleResolve}>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Resolve
                      </Button>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-white/5">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gold-500/10 flex items-center justify-center">
                      <User className="w-4 h-4 text-gold-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-text-primary">{selectedTicket.user.name}</p>
                      <p className="text-xs text-text-muted">{selectedTicket.user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    <Tag className="w-4 h-4 text-text-muted" />
                    <span className="text-sm text-text-muted">{categoryLabels[selectedTicket.category]}</span>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {selectedTicket.messages.map((message) => (
                  <div
                    key={message.id}
                    className={cn(
                      'flex',
                      message.sender === 'admin' ? 'justify-end' : 'justify-start'
                    )}
                  >
                    <div
                      className={cn(
                        'max-w-[80%] rounded-vault-lg p-4',
                        message.sender === 'admin'
                          ? 'bg-gold-500/10 border border-gold-500/20'
                          : 'bg-surface-2 border border-white/10'
                      )}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium text-text-primary">
                          {message.senderName}
                        </span>
                        <span className="text-xs text-text-muted">
                          {formatDate(message.timestamp)}
                        </span>
                      </div>
                      <p className="text-sm text-text-secondary leading-relaxed">
                        {message.content}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply Box */}
              {selectedTicket.status !== 'resolved' && selectedTicket.status !== 'closed' && (
                <div className="p-4 border-t border-white/10">
                  <div className="flex gap-3">
                    <Textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type your reply..."
                      rows={2}
                      className="flex-1"
                    />
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="primary"
                        onClick={handleSendReply}
                        loading={isSending}
                        disabled={!replyText.trim()}
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Paperclip className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          ) : (
            <Card className="flex items-center justify-center h-[calc(100vh-16rem)]">
              <div className="text-center">
                <MessageSquare className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <h3 className="text-lg font-medium text-text-primary mb-2">
                  Select a Ticket
                </h3>
                <p className="text-text-muted">
                  Choose a support ticket from the list to view details
                </p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};
